import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import searchicon from "../../../assets/icons/searchIcon.svg";
import addIcon from "../../../assets/icons/add-circle.svg";
import { useSelector } from "react-redux";
import axios from "axios";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { Col, Dropdown, Empty, Modal, Row, Space, Spin } from "antd";
import "./ReferList.scss";
import { useNavigate } from "react-router";
import moment from "moment";
import PropTypes from "prop-types";
import ReactTable from "../../../components/ReactTable/ReactTable";

const ReferList = ({ param }) => {
  // State for filters and pagination
  const [paramsValue, setParamsValue] = useState({
    page: 1,
    page_size: 10,
    plan_type: null,
    account_size: null,
    search: "",
  });
  const [affiliateList, setAffiliateList] = useState({ results: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(paramsValue.page_size);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalValues, setModalValues] = useState({});
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const { ACCtoken } = useSelector((state) => state);
  const [serverSide, setServerSide] = useState(true); // set to false for client-side

  // Debounced search handler
  const handleSearchChange = useCallback((e) => {
    const value = e?.target?.value || "";
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setParamsValue((prev) => ({ ...prev, search: value, page: 1 }));
    }, 300);
  }, []);

  // Cleanup search timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Calculate total pages

  // Fetch affiliate data
  const fetchAffiliate = useCallback(async () => {
    setLoading(true);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const config = {
        headers: {
          Authorization: ACCtoken,
        },
        params: {
          ...paramsValue,
          ...(param.start_date && {
            start_date: moment(param.start_date).format("DD/MMM/YYYY"),
          }),
          ...(param.end_date && {
            end_date: moment(param.end_date).format("DD/MMM/YYYY"),
          }),
        },
        signal: abortControllerRef.current.signal,
      };
      const response = await axios.get(
        "/affiliate/user/referred-users/list/",
        config
      );
      setAffiliateList(response?.data || { results: [], count: 0 });
    } catch (error) {
      if (axios.isCancel(error)) {
        // Ignore abort errors
        return;
      }
      console.error("Error fetching affiliates:", error);
      setAffiliateList({ results: [], count: 0 });
      toast.error("Failed to fetch affiliate data. Please try again.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [ACCtoken, paramsValue, param]);

  useEffect(() => {
    fetchAffiliate();
  }, [fetchAffiliate]);

  // Toast on copy
  const handleCopyToClipboard = useCallback((text) => {
    if (text) {
      toast.success("Affiliate Code Copied!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
        className: "custom-toast-container",
      });
    }
  }, []);

  // Filter options
  const accountTypeOptions = [
    { label: "All", value: null },
    { label: "Alpha One", value: "Alpha One" },
    { label: "Alpha Pro (6%,8%)", value: "Alpha pro" },
    { label: "Alpha Pro (10%)", value: "Alpha Pro" },
    { label: "Alpha Three", value: "Alpha Three" },
    { label: "Swing", value: "Swing" },
    { label: "Free Trial", value: "Free Trial" },
  ];

  const challengeTypeOptions = [
    { label: "All", value: null },
    { label: "5K", value: 5000 },
    { label: "10K", value: 10000 },
    { label: "25K", value: 25000 },
    { label: "50K", value: 50000 },
    { label: "100K", value: 100000 },
    { label: "200K", value: 200000 },
  ];

  // Filter handlers
  const handleAccountTypeSelect = useCallback((option) => {
    setParamsValue((prev) => ({ ...prev, plan_type: option.value, page: 1 }));
  }, []);

  const handleChallengeTypeSelect = useCallback((option) => {
    setParamsValue((prev) => ({
      ...prev,
      account_size: option.value,
      page: 1,
    }));
  }, []);

  // Dropdown menu for filters
  const menu = (
    <div className="affiliate-filter-dropdown">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <h3 className="filter-title">Plan Type</h3>
          <div className="first-tabs">
            {accountTypeOptions.map((option) => (
              <p
                key={option.label}
                onClick={() => handleAccountTypeSelect(option)}
                className={`tabs ${
                  option.value === paramsValue.plan_type ? "active" : ""
                }`}
              >
                {option.label}
              </p>
            ))}
          </div>
        </Col>
        <Col span={12}>
          <h3 className="filter-title">Account Size</h3>
          <div className="first-tabs">
            {challengeTypeOptions.map((option) => (
              <p
                key={option.label}
                onClick={() => handleChallengeTypeSelect(option)}
                className={`tabs ${
                  option.value === paramsValue.account_size ? "active" : ""
                }`}
              >
                {option.label}
              </p>
            ))}
          </div>
        </Col>
      </Row>
    </div>
  );

  // Define columns for ReactTable
  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        width: 150,
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Country/Region",
        accessor: "country",
        width: 140,
        Cell: ({ value }) => {
          if (!value) return "N/A";
          if (typeof value === "string" && value.includes("Northern Ireland")) {
            return "Northern Ireland";
          }
          if (Array.isArray(value) && value.includes("Northern Ireland")) {
            return "Northern Ireland";
          }
          return value || "N/A";
        },
      },
      {
        Header: "Date",
        accessor: "date",
        width: 120,
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : "N/A",
      },
      {
        Header: "Account Size",
        accessor: "account_size",
        width: 130,
        Cell: ({ value }) =>
          value ? `$${parseInt(value).toLocaleString()}` : "N/A",
      },
      {
        Header: "Account Price",
        accessor: "account_price",
        width: 120,
        Cell: ({ value }) =>
          value ? `$${parseInt(value).toLocaleString()}` : "N/A",
      },
      {
        Header: "Affiliate Code",
        accessor: "affiliate_Code",
        width: 120,
        Cell: ({ value }) =>
          value ? (
            <div className="aff_code_url">
              <span title={value}>
                {value.length > 20 ? `${value.substring(0, 20)}...` : value}
              </span>
              <CopyToClipboard
                onCopy={() => handleCopyToClipboard(value)}
                text={value}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="copy_icon"
                  style={{ cursor: "pointer", marginLeft: "8px" }}
                >
                  <path
                    d="M20 2H8C6.9 2 6 2.9 6 4V16C6 17.1 6.9 18 8 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H8V4H20V16ZM4 6H2V20C2 21.1 2.9 22 4 22H18V20H4V6Z"
                    fill="#B0B0B0"
                  />
                </svg>
              </CopyToClipboard>
            </div>
          ) : (
            "N/A"
          ),
      },
      {
        Header: "Commission Earned",
        accessor: "amount",
        width: 120,
        Cell: ({ value }) => (value ? `$${Number(value).toFixed(2)}` : "$0.00"),
      },
      {
        Header: "Commission %",
        accessor: "commission_percentage",
        width: 120,
        Cell: ({ value }) => (value ? `${value}%` : "0%"),
      },
      {
        Header: "Action",
        id: "action",
        width: 120,
        Cell: ({ row }) => (
          <div
            className="view-details-btn"
            onClick={() => {
              setIsModalOpen(true);
              setModalValues(row.original);
            }}
          >
            View Details
          </div>
        ),
      },
    ],
    [handleCopyToClipboard]
  );

  // Main render
  return (
    <div
      className="LeaderboardPage PaymentHistoryPage affiliateTable"
      style={{ position: "relative", width: "100%" }}
    >
      <div className="HistoryDesk">
        <div className="table_header">
          <div className="table_header_left">
            <div className="search_bar_wrapper">
              <img src={searchicon} alt="search icon" />
              <input
                type="text"
                className="search_input"
                placeholder="Search..."
                onChange={handleSearchChange}
                style={{ borderRadius: "0px" }}
              />
            </div>
          </div>
          <div style={{ marginLeft: "16px" }}>
            <Dropdown overlay={menu} trigger={["click"]}>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="affiliate-filter-btn"
              >
                <Space>Filters</Space>
              </a>
            </Dropdown>
          </div>
          <div
            onClick={() => navigate("/affiliateMarketing/create-affiliate")}
            style={{ cursor: "pointer" }}
            className="create_btn"
          >
            {/* <img src={addIcon} alt="add icon" />
            <p>Create Affiliate Code</p> */}
          </div>
        </div>
        {!affiliateList.results.length ? (
          <div className="no-data">
            <Empty description="No affiliates found" />
          </div>
        ) : (
          <>
            <Spin spinning={loading}>
              <div className="affiliateTableContainer">
                <ReactTable
                  columns={columns}
                  data={
                    serverSide ? affiliateList?.results : affiliateList?.results
                  }
                  manualPagination={serverSide}
                  page={paramsValue.page}
                  pageSize={pageSize}
                  onPageChange={(page) =>
                    setParamsValue((prev) => ({ ...prev, page }))
                  }
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setParamsValue((prev) => ({
                      ...prev,
                      page: 1,
                      page_size: newPageSize,
                    }));
                  }}
                  totalCount={affiliateList.count}
                />
              </div>
            </Spin>
            <ModalCard
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              modalValues={modalValues}
            />
          </>
        )}
      </div>
    </div>
  );
};

ReferList.propTypes = {
  param: PropTypes.shape({
    start_date: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    end_date: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }).isRequired,
};

const ModalCard = ({ isModalOpen, setIsModalOpen, modalValues }) => {
  return (
    <Modal
      title="Details"
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      footer={null}
      className="details-modal"
    >
      <p>
        <span>Challenge Type</span>{" "}
        <span>{modalValues?.challenge_type || "N/A"}</span>
      </p>
      {/* <p>
        <span>Commission Status</span>{" "}
        <span className={modalValues?.status}>
          {modalValues?.status || "N/A"}
        </span>
      </p> */}
      <p>
        <span>Payout Method</span>{" "}
        <span style={{ textTransform: "capitalize" }}>
          {modalValues?.payment_method || "N/A"}
        </span>
      </p>
      <p>
        <span>Payment Cycle</span>{" "}
        <span>{modalValues?.payment_cycle || "N/A"}</span>
      </p>
    </Modal>
  );
};

ModalCard.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  modalValues: PropTypes.object.isRequired,
};

export default ReferList;
